import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'

  import CourseExamResults from '@/views/CourseExamResults.vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import CourseExamHeader from '@/components/course/CourseExamHeader.vue'
  import CourseExamContent from '@/components/course/CourseExamContent.vue'

  import { useRouter } from 'vue-router/composables'
  import useCourse from '@/composables/useCourse'
  import useSegment from '@/composables/useSegment'
  import useI18n from '@/composables/useI18n'
  import useCommonMixin from '@/composables/useCommonMixin'

  import { CourseModule } from '@/store/modules/course'

  import CourseApi from '@/services/api/CourseApi'

  import CookieHelper from '@/helpers/CookieHelper'
  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseExam',
  setup(__props) {

  const router = useRouter()

  const {
    course,
    currentCourseId,
    courseExamId,
    isCourseExamPaused,
    isCourseExamTimedOut,
    inCourseExamResultsPage,
    currentExamQuestion,
    reviewingQuestion,
    totalPartQuestions,
    examTimeRemaining,
    latestExamCompletionId,
    examCompletionIdParam,
    answeredQuestions,
    isCourseExamTrialEnded,
  } = useCourse()
  const { examStarted, examTimedOut, examPaused, examContinued } = useSegment()
  const { isKnowledgeCheckCompany } = useCommonMixin()
  const { translateString } = useI18n()

  const timeRemainingValue = ref(0)
  const timerStarted = ref(false)
  const intervalId = ref(0)
  const showPartOneDone = ref(false)
  const noQuestionsAnsweredYet = ref(true)
  const continueExamRequest = ref(false)

  const loadingCourseContent = computed(() => course.value === null)

  const loadingExamContent = computed(() => {
    return currentExamQuestion.value === null || loadingExamCompletionState.value || reviewingQuestion.value
      ? answeredQuestions.value.length === 0
      : false
  })

  const loadingExamCompletionState = computed(() => CourseModule.fetchingExamCompletionState)

  const progressBarOptions = computed(() => {
    return course.value?.exam?.parts.map((_part, index) => {
      let percentage = 0
      if (index < currentPartIndex.value && !inCourseExamResultsPage.value) percentage = 100
      else if (index === currentPartIndex.value) {
        const qNo = currentExamQuestion.value?.questionNumberOnPart || 1
        // const fullyAnsweredPart = showPartOneDone.value && qNo === totalPartQuestions.value(index)
        if (showPartOneDone.value || inCourseExamResultsPage.value) percentage = 100
        else {
          percentage = Math.round(((qNo - 1) / totalPartQuestions.value(index)) * 100)
        }
      } else if (inCourseExamResultsPage.value) percentage = 100
      return {
        name: `${translateString('courses.examSimulation.part')} ${index + 1}`,
        percentage,
        totalPartQuestions: totalPartQuestions.value(index),
      }
    })
  })

  const currentPartIndex = computed(() => {
    if (!showPartOneDone.value && currentExamQuestion.value?.partId) {
      return course.value?.exam!.parts.findIndex((part) => part.partId === currentExamQuestion.value!.partId) ?? -1
    }
    return 0
  })

  const currentPartName = computed(() => {
    if (!course.value?.exam) return ''
    return course.value!.exam.parts[currentPartIndex.value].name
  })

  const showDuration = computed(() => {
    return inCourseExamResultsPage.value || showPartOneDone.value
      ? translateString('courses.examSimulation.done')
      : formatTimeRemaining.value
  })

  const formatTimeRemaining = computed(() => {
    const min = Math.floor(timeRemainingValue.value / 60)
    const secs = timeRemainingValue.value - min * 60
    return `${min.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  })

  const currentPartDuration = computed(() => {
    return (course.value?.exam!.parts[currentPartIndex.value].durationInMinutes || 0) * 60
  })

  const timeRemaining = () => {
    return examTimeRemaining.value || currentPartDuration.value
  }

  const examHasTimedOut = async () => {
    await CourseModule.postExamAnswer({
      selectedAnswerIds: [],
      state: 'timedOut',
      numberAnswer: null,
    }).then(() => {
      examTimedOut(
        courseExamId.value,
        totalPartQuestions.value(currentPartIndex.value),
        currentExamQuestion.value?.questionNumberOnPart ? currentExamQuestion.value?.questionNumberOnPart - 1 : 1,
        currentCourseId.value,
        course.value!.title,
      )
    })
    openExamTimeoutModal()
  }

  const openExamTimeoutModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'CourseExamTimeoutModal',
      cssClass: 'course-exam-timeout-modal',
      hideCloseButton: true,
      modalCloseCallback: () => {
        return
      },
    })
  }

  const pauseExam = () => {
    if (!latestExamCompletionId.value) return

    CourseModule.pauseExam(timeRemainingValue.value).then(() => {
      stopTimer()
      examPaused(currentCourseId.value, course.value!.title, courseExamId.value, timeRemainingValue.value)
    })
  }

  const pauseOrEndExam = () => {
    if (
      inCourseExamResultsPage.value ||
      reviewingQuestion.value ||
      noQuestionsAnsweredYet.value ||
      isCourseExamTrialEnded.value
    ) {
      router.push({ name: isKnowledgeCheckCompany.value ? 'KnowledgeCheckInfo' : 'ExamStartPage' }).catch(() => {
        return
      })
    } else {
      pauseExam()
    }
  }

  const continueExam = () => {
    continueExamRequest.value = true
    CourseModule.getNextQuestion(true)
      .then(() => {
        CookieHelper.removeCookie('exam_paused_by_system')
        examContinued(
          currentCourseId.value,
          course.value!.title,
          courseExamId.value,
          currentExamQuestion.value?.timeRemainingInSeconds ?? 0,
        )
      })
      .finally(() => {
        timeRemainingValue.value = currentExamQuestion.value?.timeRemainingInSeconds ?? 0
        if (showPartOneDone.value) {
          showPartOneDone.value = false
        }
        continueExamRequest.value = false
        if (!timerStarted.value) {
          startTimer()
        }
      })
  }

  const stopTimer = () => {
    timerStarted.value = false
    window.clearInterval(intervalId.value)
  }

  const startTimer = () => {
    timerStarted.value = true
    intervalId.value = window.setInterval(() => {
      timeRemainingValue.value--
      if (timeRemainingValue.value <= 0) {
        examHasTimedOut()
        stopTimer()
      }
    }, 1000)
  }

  const setTimeAndStartIt = () => {
    if (!isCourseExamTimedOut.value && !timerStarted.value) {
      timeRemainingValue.value = timeRemaining()
      startTimer()
    }
  }

  const setTimeRemaining = () => {
    if (isCourseExamPaused.value && examTimeRemaining.value) timeRemainingValue.value = examTimeRemaining.value
  }

  const setTime = () => {
    timeRemainingValue.value =
      isCourseExamPaused.value && examTimeRemaining.value ? examTimeRemaining.value : currentPartDuration.value
  }

  const updateCompletionTimeRemaining = () => {
    stopTimer()
    if (
      latestExamCompletionId.value &&
      !isCourseExamPaused.value &&
      !isCourseExamTimedOut.value &&
      !reviewingQuestion.value &&
      !inCourseExamResultsPage.value
    ) {
      CourseApi.updateExamCompletionTimeRemaining(
        course.value!.id,
        course.value!.exam!.id,
        latestExamCompletionId.value,
        timeRemainingValue.value,
      )
    }
  }

  const getCompletionStatusAndSetTimeRemaining = () => {
    if (latestExamCompletionId.value) {
      CourseModule.getExamCompletionState(
        reviewingQuestion.value ? examCompletionIdParam.value : latestExamCompletionId.value,
      ).finally(() => {
        setTimeRemaining()
      })
    }
  }

  const scrollFixedExamContainerToTop = () => {
    const fixedExamContainer = document.querySelector('.course-exam-container')
    if (fixedExamContainer) {
      fixedExamContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const scrollFixedExamContainerToBottom = () => {
    const fixedExamContainer = document.querySelector('.course-exam-container')
    if (fixedExamContainer) {
      fixedExamContainer.scrollTo({ top: fixedExamContainer.scrollHeight, behavior: 'smooth' })
    }
  }

  watch(currentExamQuestion, (value) => {
    if (value) {
      if (
        !loadingExamContent.value &&
        !inCourseExamResultsPage.value &&
        !reviewingQuestion.value &&
        !isCourseExamPaused.value
      ) {
        setTimeAndStartIt()
        if (value.questionNumber === 1) {
          examStarted(courseExamId.value, currentCourseId.value, course.value!.title)
        }
      }
    }
  })

  watch(inCourseExamResultsPage, (value) => {
    if (value) {
      CourseModule.getCourseExamQuestionsAnswered(examCompletionIdParam.value)
      stopTimer()
    }
  })

  watch(reviewingQuestion, (value) => {
    if (value) {
      stopTimer()
    }
  })

  watch(showPartOneDone, (value) => {
    if (value) {
      CookieHelper.setCookieValue('exam_paused_by_system', 'true')
      pauseExam()
    } else {
      setTime()
    }
  })

  if (!course.value) {
    CourseModule.getCourse({ courseId: currentCourseId.value }).finally(() => {
      getCompletionStatusAndSetTimeRemaining()
      if (!CourseModule.coursesModuleMap[currentCourseId.value]) {
        CourseModule.getCourseModules(currentCourseId.value)
      }
    })
  } else if (!inCourseExamResultsPage.value) {
    getCompletionStatusAndSetTimeRemaining()
  }

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', updateCompletionTimeRemaining)
  })

  onMounted(() => {
    window.addEventListener('beforeunload', updateCompletionTimeRemaining)
  })

return { __sfc: true,router, course, currentCourseId, courseExamId, isCourseExamPaused, isCourseExamTimedOut, inCourseExamResultsPage, currentExamQuestion, reviewingQuestion, totalPartQuestions, examTimeRemaining, latestExamCompletionId, examCompletionIdParam, answeredQuestions, isCourseExamTrialEnded, examStarted, examTimedOut, examPaused, examContinued, isKnowledgeCheckCompany, translateString, timeRemainingValue, timerStarted, intervalId, showPartOneDone, noQuestionsAnsweredYet, continueExamRequest, loadingCourseContent, loadingExamContent, loadingExamCompletionState, progressBarOptions, currentPartIndex, currentPartName, showDuration, formatTimeRemaining, currentPartDuration, timeRemaining, examHasTimedOut, openExamTimeoutModal, pauseExam, pauseOrEndExam, continueExam, stopTimer, startTimer, setTimeAndStartIt, setTimeRemaining, setTime, updateCompletionTimeRemaining, getCompletionStatusAndSetTimeRemaining, scrollFixedExamContainerToTop, scrollFixedExamContainerToBottom, computed, ref, watch, onBeforeUnmount, onMounted, CourseExamResults, LoadingSpinner, CourseExamHeader, CourseExamContent, useRouter, useCourse, useSegment, useI18n, useCommonMixin, CourseModule, CourseApi, CookieHelper, eventBus }
}

})